import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '../constants';

const LiveDonationToast = () => {
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const lastSeenRef = useRef(null);
  const timeoutRef = useRef(null);
  const initialLoadRef = useRef(true);

  const showToast = useCallback((donation) => {
    setToast(donation);
    setVisible(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => setToast(null), 500);
    }, 6000);
  }, []);

  const checkForNew = useCallback(async () => {
    try {
      const res = await fetch(`${API}/donations/wall`);
      const data = await res.json();
      if (!Array.isArray(data) || data.length === 0) return;

      const latest = data[0];
      const latestKey = `${latest.name}_${latest.amount}_${latest.timestamp}`;

      if (initialLoadRef.current) {
        lastSeenRef.current = latestKey;
        initialLoadRef.current = false;
        // Show the most recent donation after a delay for social proof
        setTimeout(() => showToast(latest), 8000);
        return;
      }

      if (latestKey !== lastSeenRef.current) {
        lastSeenRef.current = latestKey;
        showToast(latest);
      }
    } catch {}
  }, [showToast]);

  useEffect(() => {
    checkForNew();
    const interval = setInterval(checkForNew, 30000);
    return () => { clearInterval(interval); clearTimeout(timeoutRef.current); };
  }, [checkForNew]);

  const dismiss = () => {
    setVisible(false);
    setTimeout(() => setToast(null), 500);
  };

  if (!toast) return null;

  const isAnon = toast.name === 'Anonimo';
  const displayName = isAnon ? 'Qualcuno' : (toast.company_name || toast.name);
  const location = toast.comune ? ` da ${toast.comune}` : '';

  return (
    <div
      data-testid="live-donation-toast"
      className={`fixed bottom-24 right-6 z-[9998] max-w-sm transition-all duration-500 ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-[120%] opacity-0'
      }`}
    >
      <div
        className="bg-[#0A0A0A] border border-[#00FF41]/30 rounded-lg overflow-hidden shadow-2xl"
        style={{ boxShadow: '0 0 30px rgba(0,255,65,0.12)' }}
      >
        {/* Glowing top bar */}
        <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, transparent, ${toast.belt_color || '#00FF41'}, transparent)` }} />

        <div className="px-4 py-3 flex items-center gap-3">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center flex-shrink-0 bg-black"
            style={{ borderColor: toast.belt_color || '#00FF41' }}>
            {toast.brand_logo_url ? (
              <img src={toast.brand_logo_url} alt="" className="w-full h-full object-contain rounded-full p-0.5" />
            ) : isAnon ? (
              <svg className="w-5 h-5 text-zinc-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
              </svg>
            ) : (
              <span className="font-mono font-black text-lg" style={{ color: toast.belt_color || '#00FF41' }}>
                {displayName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-bold truncate">
              {displayName}
              <span className="text-zinc-500 font-normal">{location}</span>
            </p>
            <p className="text-zinc-400 text-xs">
              ha donato{' '}
              <span className="font-black" style={{ color: toast.belt_color || '#00FF41' }}>{toast.amount}€</span>
              {' · '}
              <span style={{ color: toast.belt_color || '#666' }}>{toast.belt}</span>
            </p>
          </div>

          {/* Close */}
          <button
            onClick={dismiss}
            data-testid="toast-dismiss"
            className="text-zinc-600 hover:text-white transition-colors flex-shrink-0 ml-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Auto-dismiss progress bar */}
        {visible && (
          <div className="h-0.5 bg-zinc-800">
            <div
              className="h-full"
              style={{
                background: toast.belt_color || '#00FF41',
                animation: 'toastProgress 6s linear forwards',
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes toastProgress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default LiveDonationToast;
